import type { IFormComponent, IFormStructure } from '@/modules/Administracion/interfaces'
import { pdfTableLayout, listColums } from './pdfStyles'
import { isValidHeader, tipoImpresionAsistencialDefault } from '../constantes'
import type { ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import { getDiagnosticData } from './diagnosticData'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
interface IText {
    text: string,
    style?: string
}
interface IBody {
   text: string | IText |IText[],
   border?: boolean[],
   colSpan?: number,
   style?: string,
   table?: {
       widths: number[],
       body: IBody[][]
   },
    layout?: object,
    [key: string]: unknown
}

/**
 * Genera una representación visual de los componentes para el PDF usando tabla y layout unificado.
 * @param components Lista de componentes a procesar
 * @returns Objeto con la estructura para el PDF
 */

export function processComponents(components: IFormComponent[], tipo: string, fechaStr?: string, tecnologiaNombre?: string) {
  if (!components || components.length === 0) {
    return {
      text: '',
      margin: [0, 0, 0, 0]
    }
  }

  // Recorrido recursivo para soportar secciones anidadas y componentes individuales
  const renderComponent = (component: IFormComponent, isTopLevel = false) => {
    if (component.typeComponent === 'SECTION') {
      return renderSection(component, isTopLevel)
    }
  }

  const getText = (item: IFormComponent, isLaboratorio: boolean = false): IBody[] => {
    const colSpan = listColums[item.properties?.columns as string]
    const itemTable:IBody[] = []
    let value = (item.value || '')
    if(item.typeComponent === 'LIST'){
      value = item.options?.find(e => e.val == item.value)?.text || ''
    } else if(item.typeComponent === 'LIST_SEARCH'){
      value = item.searchValue || ''
    }
    if (!isLaboratorio) {
      itemTable.push({ text: [{text: `${item.label}:\n`, style: 'tituloCell' },{text: ' ' + value , style: 'textoCell'}],  border: [false, false, false, false], colSpan: colSpan})
    } else {
      itemTable.push(
        { text: [{text: (item.label || '') , style: 'tituloCell'}],  border: [false, false, false, false], colSpan: 3},
        { text: '', border: [false, false, false, false] },
        { text: '', border: [false, false, false, false] },
        { text: [{text: (item.value || '') , style: 'textoCell'}],  border: [false, false, false, false], colSpan: 3},
        { text: '', border: [false, false, false, false] },
        { text: '', border: [false, false, false, false] },
        { text: [{text: (item.referencia || '') , style: 'tituloCell'}],  border: [false, false, false, false], colSpan: 3},
        { text: '', border: [false, false, false, false] },
        { text: '', border: [false, false, false, false] },
        { text: [{text: (item.unidad || '') , style: 'tituloCell'}],  border: [false, false, false, false], colSpan: 3},
        { text: '', border: [false, false, false, false] },
        { text: '', border: [false, false, false, false] }
      )
    }
    return itemTable
  }

  const rellenarFaltantes = (body: (IBody | string)[], count: number) => {
    body.push(...Array(count).fill({ text: '', border: [false, false, false, false] }));
  }
  function renderSection(component: IFormComponent, isTopLevel = false): { style: string, table: { widths: string[], body: (IBody | string)[][] }, layout?: object | null } {
    const header: unknown[] = []
    if (isTopLevel && fechaStr && tecnologiaNombre) {
      header.push([
        { text: fechaStr, style: 'tituloCell', colSpan: 6, border: [false, false, false, false], fillColor: '#e8f0fe' },
        ...Array(5).fill({ text: '', border: [false, false, false, false] }),
        { text: tecnologiaNombre, style: 'tituloCell', alignment: 'right', colSpan: 6, border: [false, false, false, false], fillColor: '#e8f0fe' },
        ...Array(5).fill({ text: '', border: [false, false, false, false] }),
      ])
    }
    const components = component.components || []
    const isFatherSections = component.typeComponent === 'SECTION' && components.some(e => e.typeComponent === 'SECTION');
    const isLaboratorio = tipo === tipoImpresionAsistencialDefault.LABORATORIOS;
    const isVacuna = tipo === tipoImpresionAsistencialDefault.VACUNA;
    if (isLaboratorio && isFatherSections) {
      header.push([
        { text: (component.multi? '#' + component.item + ' ': '' )  + component.label, style: 'textoCell', colSpan: 12, border: [false, false, false, false], fillColor: '#f2f9ff' },
        ...Array(11).fill({})
      ])
    } else if (isLaboratorio) {
      header.push([
        { text: 'AREA: '+ component.label, style: 'textoCell', colSpan: 12, border: [false, false, false, false], fillColor: '#f2f9ff' },
        ...Array(11).fill({})
      ])
      header.push([
        { text: 'VARIABLE:', style: 'textoCell', colSpan: 3, border: [false, false, false, false] },
        ...Array(2).fill({}),
        { text: 'RESULTADO:', style: 'textoCell', colSpan: 3, border: [false, false, false, false] },
        ...Array(2).fill({}),
        { text: 'REFERENCIA:', style: 'textoCell', colSpan: 3, border: [false, false, false, false] },
        ...Array(2).fill({}),
        { text: 'UNIDAD:', style: 'textoCell', colSpan: 3, border: [false, false, false, false] },
      ])
    } else if (isVacuna) {
      header.push([
        { text: '' , style: 'textoCell', colSpan: 12, border: [false, false, false, false], fillColor: '#f2f9ff' },
        ...Array(11).fill({})
      ])
    }  else {
      header.push([
        { text: (component.multi? '#' + component.item + ' ': '' ) + component.label, alignment: 'center', style: 'subheader', colSpan: 12 },
        ...Array(11).fill({})
      ])
    }

    const bodys: (IBody | string)[][] = [];
    let body: (IBody | string)[] = [];
    let colSpanRow = 0;

    for (let i = 0; i < components.length; i++) {
      const item = components[i];

      // Detectar sección anidada
      if (item.typeComponent === 'SECTION') {
        // Cerrar fila actual si hay contenido pendiente
        if (colSpanRow > 0 && body.length > 0) {
          if (colSpanRow < 12) {
            rellenarFaltantes(body, 12 - colSpanRow)
          }
          bodys.push(body)
          body = []
          colSpanRow = 0
        }

        // Llamada recursiva al renderizador (secciones anidadas no son top-level)
        const nested = renderComponent(item, false);
        if (nested && nested.table && nested.table.body) {
          bodys.push([{ ...nested, colSpan: 12 }, ...Array(11).fill({})]);
        }
        continue;
      }

      // Procesamiento de campo simple
      let colSpan = !isLaboratorio ? listColums[item.properties?.columns as string] || 3 : 3;
      if((colSpanRow + colSpan) > 12){
        rellenarFaltantes(body, 12 - colSpanRow)
        bodys.push(body)
        body = []
        colSpanRow = 0
      }
      colSpan = isLaboratorio ? 12 : colSpan;
      const textoCell = getText(item, isLaboratorio);
      body.push(...textoCell);
      if(!isLaboratorio )rellenarFaltantes(body, colSpan - 1);
      colSpanRow += isLaboratorio ? 12 : colSpan;

      const isLast = components.length - 1 == i
      if (colSpanRow <= 12 && isLast) {
        if (colSpanRow < 12) rellenarFaltantes(body, 12 - colSpanRow)
        bodys.push(body)
        body = []
        colSpanRow = 0
      }
    }

    return {
      style: !isLaboratorio ? 'tableHistoria':'',
      table: {
        widths: Array(12).fill("*"),
        body: [
          ...header,
          ...bodys
        ] as IBody[][]
      },
      layout: !isLaboratorio ? pdfTableLayout: null,
    };
  }

  // Renderizar todos los componentes/tabs
  const sections = components.map(item => renderComponent(item, true)).filter(Boolean)
  return {
    stack: sections,
    margin: [0, 10, 0, 20]
  }
}

/**
 * Función mejorada para procesar componentes y generar representación PDF
 * @param components Lista de componentes a procesar
 * @param content Array donde se agregarán los elementos procesados
 */
function forComponents(components: IFormComponent[], tipo: string, fechaStr?: string, tecnologiaNombre?: string) {
  if (!components || components.length === 0) return
  const processedContent = processComponents(components, tipo, fechaStr, tecnologiaNombre)
  return processedContent
}

export const getComponentsData = (contenido: IFormStructure, technologia: ICitaTechnologiasSalud , tipo: string, datosPaciente: unknown[], fechaStr?: string, tecnologiaNombre?: string) => {
  const content = [] as unknown[]
  if (isValidHeader(datosPaciente)) {
    content.push(datosPaciente)
  }
  if (tipo === tipoImpresionAsistencialDefault.HISTORIA || tipo === tipoImpresionAsistencialDefault.PROCEDIMIENTOS || tipo === tipoImpresionAsistencialDefault['OTROS SERVICIOS']) {
    // Procesar componentes SOLO si hay componentes
    if (Array.isArray(contenido.components) && contenido.components.length > 0) {
      content.push(forComponents(contenido.components, tipo, fechaStr, tecnologiaNombre))
    }
  }
  else if (tipo === tipoImpresionAsistencialDefault.LABORATORIOS || tipo === tipoImpresionAsistencialDefault.VACUNA) {
    if (Array.isArray(contenido.components) && contenido.components.length > 0) {
      const componentsFiltred = contenido.components
      const headersLaboratorios = renderHeadersLaboratorios(componentsFiltred)
      headersLaboratorios.forEach(header => {
        const contentLab = [] as unknown[]
        componentsFiltred.filter(component => `${component.codigo + '. ' || ''} ${component.tecnologia}` === header.text).forEach(component => {
          console.log(component)
          contentLab.push(forComponents([ component ], tipo))
        })
        content.push({
          style: 'tableHistoria',
          table: {
            widths: Array(1).fill("*"),
            body: [
              [header],
              [contentLab]
            ]
          },
          layout: pdfTableLayout,
        })
      })
    }
  }
  const diagnosticos = technologia.diagnosticos || contenido.diagnosticos || []
  const maestras = contenido.maestras as IConfMaestra[] || []
  if (diagnosticos && diagnosticos.length > 0 && tipo === tipoImpresionAsistencialDefault.HISTORIA ) {
    content.push(getDiagnosticData(diagnosticos, maestras))
  }
  return content
}

const renderHeadersLaboratorios = (headers: IFormComponent[]) => {
  if (!headers || headers.length === 0) return []

  const headerContent = headers.filter((item, index, self) =>
    index === self.findIndex(h => h.codigo === item.codigo)
  ).map(header => {
    return {
      text: `${header.codigo + '. ' || ''} ${header.tecnologia}`,
      style: 'subheaderTech'
    }
  })

  return headerContent
}
